import React, { type ComponentProps } from "react";
import { type GetStaticProps, type NextPage } from "next";
import Image from "next/image";
import { Text, SimpleGrid, Box, Flex } from "@chakra-ui/react";
import { trackEvent } from "lib/analytics";
import {
  getPage,
  getAnnouncementBanner,
  getStackedLogos,
  getSimpleIntegrations,
  type Page as SanityPage,
  type IntegrationSimple,
  type Customer,
  type Banner,
} from "lib/sanity";
import { Page } from "components/page";
import { HalfHeroAnimation } from "components/half-hero-animation";
import { MatchBoosterAnimation } from "components/match-booster-animation";
import { StackedLogos } from "components/logos";
import { Section } from "components/section";
import { SectionHeading } from "components/section-heading";
import { Feature } from "components/feature";
import { QuoteSlider, Quote } from "components/quotes";
import DestinationMarquee from "components/destination-marquee";
import { VideoFull } from "components/video-full";
import { getPageMetaImage } from "utils";
import { PairImage } from "components/pair-image";
import fullWidthImage from "../../../public/assets/platform/match-booster/full-width.png";
import fullWidthMobileImage from "../../../public/assets/platform/match-booster/full-width-mobile.png";
import pair1Image from "../../../public/assets/platform/match-booster/pair-1.png";

type PageProps = {
  /** Only for the duplicate of this page that's used as an ads page. */
  noIndex?: boolean;
  page: SanityPage;
  announcementBanner?: Banner;
  destinations: IntegrationSimple[];
  customerRows: Customer[][];
  headerVariant?: ComponentProps<typeof Page>["headerVariant"];
};

// Note: This page is used for both /platform/match-booster here and imported by /join/match-booster-liveramp-competitor for a custom ad page
const MatchBooster: NextPage<Readonly<PageProps>> = ({
  noIndex,
  page,
  announcementBanner,
  destinations,
  customerRows,
  headerVariant,
}) => {
  return (
    <Page
      noIndex={noIndex}
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      headerVariant={headerVariant}
    >
      <HalfHeroAnimation
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        animationElement={<MatchBoosterAnimation />}
      />

      <StackedLogos customerRows={customerRows} />

      <Section center bg="infographicGradient" color="white" my={{ base: 32, md: 40 }} py={{ base: 20, md: 32 }}>
        <SectionHeading
          title="Boost your growth with data"
          description="A central and secure enrichment solution to help modern growth teams fill in customer data gaps. Affordable and always up to date. No engineering required."
        />

        <Box display={{ base: "none", md: "block" }} pos="relative" w="100%" mt={{ base: 16, md: 24 }}>
          <Image
            src={fullWidthImage}
            alt="Customer data moving from your Data Warehouse, into Hightouch's Match Booster where first party data is added, and then into your ad platforms with increased match boosting rates."
            width={1200}
            height={420}
          />
        </Box>

        <Flex
          display={{ base: "flex", md: "none" }}
          pos="relative"
          align="center"
          justify="center"
          w="100%"
          mt={{ base: 16, md: 24 }}
        >
          <Image
            src={fullWidthMobileImage}
            alt="Customer data moving from your Data Warehouse, into Hightouch's Match Booster where first party data is added, and then into your ad platforms with increased match boosting rates."
            width={250}
            height={1400}
          />
        </Flex>
      </Section>

      <Section>
        <PairImage
          image={pair1Image}
          align="left"
          title="Find your customers in every channel"
          actionText="Learn more"
          actionUrl="/blog/announcing-match-booster"
          description={
            <Text>
              Combine the power of your first party customer data with data from the world’s leading enrichment
              providers to ensure you can find and target your customers across all your marketing channels.
            </Text>
          }
        />
      </Section>

      <Section>
        <Text as="h2" textStyle="h4" textAlign={{ base: "center", md: "left" }}>
          Match Booster benefits
        </Text>

        <SimpleGrid columns={{ sm: 1, md: 3, lg: 3 }} gap={16} mt={{ base: 8, md: 16 }}>
          <Feature
            image="/assets/platform/match-booster/feature-1.svg"
            imageWidth="72px"
            title="Broader reach"
            description="Maximize audience sizes across channels to reach more potential customers with your campaigns."
          />

          <Feature
            image="/assets/platform/match-booster/feature-2.svg"
            imageWidth="72px"
            title="Better targeting"
            description="Refine your targeting to reach all of the people who will engage, while excluding those who won’t."
          />

          <Feature
            image="/assets/platform/match-booster/feature-3.svg"
            imageWidth="72px"
            title="Optimized spend"
            description="Maximize ROAS by reaching the right customers with each and every campaign."
          />
        </SimpleGrid>
      </Section>

      <QuoteSlider
        my={0}
        mb={16}
        slides={[
          <Quote
            key="velir"
            isInSlider
            quote="It’s like pushing the easy button for improving ad performance."
            author="Adam Ribaudo"
            authorImage="https://cdn.sanity.io/images/pwmfmi47/production/e630fd53899d5ea57744f7400d53abdf44f6c02a-816x720.png"
            job="VP of Data Activation"
            company="Velir"
            logo="https://cdn.sanity.io/images/pwmfmi47/production/c6761367c70cf61cb324bd9073d12eb9549340d4-359x100.svg"
          />,
          <Quote
            key="capgemini"
            isInSlider
            quote="In the post-cookie world, marketers must lean into advanced data strategies using 1st, 2nd and 3rd party data. With Customer Studio and Match Booster, Hightouch enables them to do exactly that."
            author="Dio Favatas"
            authorImage="https://cdn.sanity.io/images/pwmfmi47/production/080a09612bf9e497d21831af2efe33eaea2fea35-816x720.png"
            job="Head of Identity and Marketing Clouds"
            company="Tredence"
            logo="https://cdn.sanity.io/images/pwmfmi47/production/97560aa155c39dee4073d85f5ec7dad323c0cb68-600x66.png"
          />,
          <Quote
            key="hakkoda"
            isInSlider
            quote="With Match Booster, marketers can now unleash the true power of the data warehouse to power both AdTech and MarTech."
            author="Lee Hammond"
            authorImage="https://cdn.sanity.io/images/pwmfmi47/production/84de82be1e524d9edb566aae562d578d1a87aa35-816x720.png"
            job="Marketing Technology Lead"
            company="Hakkoda"
            logo="https://cdn.sanity.io/images/pwmfmi47/production/c581996eac6ab4bc6894e6799b820fa9f0630b18-450x299.png"
          />,
        ]}
      />

      <DestinationMarquee destinations={destinations} />

      <VideoFull
        source="https://cdn.sanity.io/files/pwmfmi47/production/a3252729ecc79b909e9cdb86ddd1c46fdf199c07.mp4"
        poster="https://cdn.sanity.io/images/pwmfmi47/production/46ed715f8d3160eae4608663f62772dab9848b14-2418x1234.webp"
        title="Learn about Hightouch Match Booster"
        description="See how Hightouch Match Booter works in this 5 minute video."
        duration="5 mins"
        sectionTitle="See how it works"
        sectionDescription="Watch this 5 minute video to learn more about Match Booster."
        onPlay={() => {
          trackEvent("Match Booster Video Played");
        }}
        onEnded={() => {
          trackEvent("Match Booster Video Ended");
        }}
      />
    </Page>
  );
};

export default MatchBooster;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, destinations, announcementBanner, customerRows] = await Promise.all([
    getPage("/platform/match-booster"),
    getSimpleIntegrations("destination"),
    getAnnouncementBanner(),
    getStackedLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      destinations,
      announcementBanner,
      customerRows,
    },
    revalidate: 60,
  };
};
